import { Stack, useMediaQuery, useTheme } from "@mui/material"
import { useCallback, useEffect, useMemo, useState } from "react"
import { DesktopControl } from "./Controls"
import { StoryContainer } from "./StoryContainer"
import { StoryTimeline } from "./StoryTimeline"
import { preloadImages } from "../../../services/utils/utils"
import { Story, StorySlide } from "../../../models/stories.models"

interface StoryViewProps {
  story: Story
}

export const StoryView = ({ story }: StoryViewProps) => {
  const theme = useTheme()
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"))

  // preload all story images
  useEffect(() => {
    const srcs: string[] = []

    for (const slide of story.slides) {
      for (const content of slide.contents) {
        if (content.media && content.media.type !== "video") {
          srcs.push(content.media.url)
        }
      }
    }

    preloadImages(srcs)
  }, [story])

  // current slide
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0)
  const currentSlide: StorySlide | undefined = useMemo(
    () => story.slides[currentSlideIndex],
    [story, currentSlideIndex]
  )

  // total number of visualized slides, used in timeline
  const [currentSlideCounter, setCurrentSlideCounter] = useState<number>(0)

  // navigation logic
  const backwardButtonDisabled: boolean = useMemo(
    () => !story.slides[currentSlideIndex - 1],
    [story, currentSlideIndex]
  )
  const forwardButtonDisabled: boolean = useMemo(
    () => !story.slides[currentSlideIndex + 1],
    [story, currentSlideIndex]
  )

  const goBackward = useCallback(() => {
    if (!backwardButtonDisabled) {
      setCurrentSlideCounter((current) => current - 1)
      setCurrentSlideIndex((current) => current - 1)
    }
  }, [backwardButtonDisabled])

  const goForward = useCallback(() => {
    if (!forwardButtonDisabled) {
      setCurrentSlideCounter((current) => current + 1)
      setCurrentSlideIndex((current) => current + 1)
    }
  }, [forwardButtonDisabled])

  // keys navigation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.code) {
        case "ArrowLeft":
          goBackward()
          break
        case "ArrowRight":
          goForward()
          break
      }
    }

    window.addEventListener("keydown", onKeyDown)

    return () => window.removeEventListener("keydown", onKeyDown)
  }, [goBackward, goForward])

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={matchesMD ? 0 : 6}
      style={{ flex: 1, minHeight: 0 }}
    >
      <Stack
        gap={6}
        direction="row"
        alignItems="center"
        justifyContent="center"
        style={{
          width: "100%",
          height: "100%",
          maxHeight: matchesMD ? undefined : 600,
          minHeight: 0,
        }}
      >
        {!matchesMD && (
          <DesktopControl
            type="backward"
            handleSlideChange={goBackward}
            hidden={backwardButtonDisabled}
          />
        )}
        <StoryContainer
          currentSlide={currentSlide}
          currentSlideIndex={currentSlideIndex}
          forwardButtonDisabled={forwardButtonDisabled}
          goBackward={goBackward}
          goForward={goForward}
          story={story}
        />
        {!matchesMD && (
          <DesktopControl
            type="forward"
            handleSlideChange={goForward}
            hidden={forwardButtonDisabled}
          />
        )}
      </Stack>
      <StoryTimeline
        story={story}
        currentSlideCounter={currentSlideCounter}
        goForward={goForward}
      />
    </Stack>
  )
}
